import { observer } from 'mobx-react';
import React, { useCallback, useMemo } from 'react';
import { useLocation } from 'react-router';

import useStores from 'hooks/use-stores';
import { SearchType } from 'main/components/SearchBar';

import useStyles from './styles';

interface Props {
    data: {
        families?: Record<string, number>;
        types?: Record<string, number>;
    };
    className?: string;
}

const Occurrences: React.FC<Props> = ({ data, className }) => {
    const location = useLocation();
    const { robotTypesStore, searchStore } = useStores();
    const { classes, cx } = useStyles();

    const typeItems = useMemo(() => {
        const types = data.types ?? {};
        return Object.keys(types)
            .sort((typeA, typeB) => types[typeB] - types[typeA])
            .map((type) => [type, robotTypesStore.get(type) || type, types[type]] as const);
    }, [data, robotTypesStore]);

    const getTypeHref = useCallback(
        (type: string) => {
            if (searchStore.type === SearchType.simple) {
                const searchParams = new URLSearchParams(location.search);
                const query = searchParams.get('query') || '';

                if (query && query.includes('type:')) {
                    searchParams.set('query', query.replace(/type:[^\s]+/, `type:${type}`));
                } else {
                    searchParams.set('query', [query, `type:${type}`].join(' and '));
                }
                return `/search?${searchParams.toString()}`;
            }
        },
        [location.search, searchStore.type]
    );

    const familyItems = useMemo(() => {
        const families = data.families ?? {};
        return Object.keys(families)
            .sort((familyA, familyB) => families[familyB] - families[familyA])
            .map((family) => [family, family[0].toUpperCase() + family.substring(1), families[family]] as const);
    }, [data]);

    const getFamilyHref = useCallback(
        (family: string) => {
            if (searchStore.type === SearchType.simple) {
                const searchParams = new URLSearchParams(location.search);
                const query = searchParams.get('query') || '';

                if (query && query.includes('bulletinFamily:')) {
                    searchParams.set('query', query.replace(/bulletinFamily:[^\s]+/, `bulletinFamily:${family}`));
                } else {
                    searchParams.set('query', [query, `bulletinFamily:${family}`].join(' and '));
                }
                return `/search?${searchParams.toString()}`;
            }
        },
        [location.search, searchStore.type]
    );

    if (typeItems.length <= 1 && familyItems.length <= 1) {
        return null;
    }

    return (
        <div className={cx(classes.root, className)}>
            {typeItems.length > 1 ? (
                <div className={classes.container}>
                    {typeItems.map(([type, label, count], index) => (
                        <a key={index} className={classes.item} href={getTypeHref(type)} rel="noreferrer">
                            <span className={classes.label}>{label}</span>
                            <span className={classes.count}>{count}</span>
                        </a>
                    ))}
                </div>
            ) : null}
            {familyItems.length > 1 ? (
                <div className={classes.container}>
                    {familyItems.map(([family, label, count], index) => (
                        <a key={index} className={classes.item} href={getFamilyHref(family)} rel="noreferrer">
                            <span className={classes.label}>{label}</span>
                            <span className={classes.count}>{count}</span>
                        </a>
                    ))}
                </div>
            ) : null}
        </div>
    );
};

export default observer(Occurrences);
